import _ from 'lodash'
import EventBus from '@/eventBus'
import Constants from '@/constant'
import SlideApi from '@/api/slide'
import {
  SET_CURRENT_SLIDE_MODE,
  EDIT_MODE,
  SET_CURRENT_ACTIVE_SLIDE,
} from '@/vuex/types'

import Socket from '@/socket'
import socketHelper from '@/helpers/socket/socket-helper'
import store from '@/vuex/store'
import storage from '@/services/storage'
import Utils from '@/util'
import workspace from '@/constant/workspace'
import OpenAISocketHandler from './open-ai/OpenAISocketHandler'
import { getMessageSubscriber } from '@/websocket/subscribers/main.websocket.subscriber'
import PresentationApi from '@/api/presentation'
import { handleAnnotationUpdated } from '@/websocket/subscribers/slide-annotations.websocket.subscriber'

const CREATE_REACTION = 'create-reaction'

const mergeDataPresentation = (data) => {
  const { slides } = store.state.presentation
  slides.forEach((slide) => {
    const index = data.slides.findIndex((item) => {
      return item.id === slide.id
    })
    if (slide.id && index !== -1) {
      if (slide.type === 'openEnded' || slide.type === 'voiceQA' || slide.type === 'wordCloud') {
        slide.SlideOptions = data.slides[index].SlideOptions
      } else if (slide.type === 'multipleChoice' || slide.type === 'imageChoice') {
        slide.SlideOptions.forEach((option) => {
          const indexOption = data.slides[index].SlideOptions.findIndex(
            (item) => {
              return item.id === option.id
            },
          )

          if (option.id && indexOption !== -1) {
            option.count =
              data.slides[index].SlideOptions[indexOption].count
          }
        })

        if (slide.id === store.state.slideActive.id) {
          store.dispatch('updateSlideActive', slide)
        }
      }
    }
  })
  store.dispatch(
    'updatePresentation',
    store.state.presentation,
  )
}

const handleSocketConnect = async () => {
  const presentationId = store.state.presentation.id
  if (
    presentationId
    && !store.state.sharePresentation
  ) {
    const payload = { socketId: Socket.getSocketId() }

    if (store.state.isAdmin) {
      payload.admin = true
    }

    let res
    try {
      res = await PresentationApi.getPresentationDetails(presentationId, payload)
    } catch (error) {
      return
    }
    const presentation = Utils.formatPresentationReconect(res.body)
    mergeDataPresentation(presentation)
  }
}

export default {
  initSocket(channel, editor) {
    Socket.subscribe(`${channel}-presenter`, (doc) => {
      this.initSocketHandler(doc, editor)
    })
  },

  initSocketHandler(doc, editor) {
    if (doc.socketId == Socket.getSocketId()) {
      return
    }

    const messageType = doc.type || doc.t
    const messageSubscriber = getMessageSubscriber(messageType)
    if (messageSubscriber) {
      if (store.state.integrationPlatform === 'GoogleSlide') {
        return
      }
      messageSubscriber(doc, editor)
    }
  },

  disconnectSocket(editor) {
    socketHelper.leaveRoomPresentation(editor.presentation.uniqueAccessCode)
    socketHelper.leaveRoomSlide(editor.presentation.uniqueAccessCode, editor.slideActive.id)

    Socket.getSocket().off('socket:online')
    Socket.getSocket().off('socket:offline')
    Socket.getSocket().off('presenter-takeover')
    Socket.getSocket().off('create-idea')
    Socket.getSocket().off('vote-idea')
    Socket.getSocket().off('presenter-removed')
    Socket.getSocket().io.off('reconnect')
    Socket.getSocket().off('add-slides')
    Socket.getSocket().off('presentation:report_generated')
    Socket.getSocket().off('backstage-control')
    Socket.off(CREATE_REACTION)
    Socket.off('patch-presentation')
    Socket.getSocket().off('conversation-presenter-share')
    Socket.off('server_timestamp')

    // AI SOCKET
    Socket.getSocket().off('presentation: ai_slides_generator_generated')
    Socket.getSocket().off('presentation: ai_slides_generator_error')
    Socket.getSocket().off('presentation: ai_template_description')
    Socket.getSocket().off('presentation: doc_to_quiz_generated')
    Socket.getSocket().off('presentation: update-slides')
    Socket.getSocket().off('slide: ai_word_cloud_smart_grouping')
    Socket.getSocket().off('slide: ai_open_ended_smart_grouping')
    Socket.getSocket().off('slide: ai_open_ended_smart_grouping_submission_change')
    Socket.getSocket().off('annotationsUpdated')

    Socket.unSubscribe(editor.$store.state.presentation.uniqueAccessCode)
    Socket.unSubscribe(`${editor.$store.state.presentation.uniqueAccessCode}-presenter`)
    Socket.unSubscribe(`user-${editor.$store.state.currentUser.id}`)
  },

  handleSocketEvent(editor) {
    Socket.getSocket().io.on('reconnect', () => this._handleOnReconnect(editor))

    Socket.getSocket().on('socket:online', (doc) => this._handleSocketOnline(editor, doc))
    Socket.getSocket().on('socket:offline', (doc) => this._handleSocketOffline(editor, doc))
    Socket.getSocket().on('presenter-takeover', (doc) => this._handleTakeOverEvent(editor, doc))
    Socket.getSocket().on('create-idea', (doc) => this._handleAudienceCreateIdeaEvent(editor, doc))
    Socket.getSocket().on('vote-idea', (doc) => this._handleAudienceVoteIdeaEvent(editor, doc))
    Socket.getSocket().on('presenter-removed', (doc) => this._handleRemovePresenterEvent(editor, doc))
    Socket.getSocket().on('add-slides', (doc) => this._handleAddSlides(editor, doc))
    Socket.getSocket().on('presentation:report_generated', (doc) => this._handleUpdateReport(editor, doc))
    Socket.getSocket().on('backstage-control', (doc) => this._handleBackstageControl(editor, doc))
    Socket.getSocket().on('conversation-presenter-share', (doc) => this._handleCommentPresentation(editor, doc))
    Socket.getSocket().on('presenting-slide-update', (doc) => this._handlePresentingSlideUpdate(editor, doc))

    // AI SOCKET
    Socket.getSocket().on('presentation: ai_slides_generator_generated', (doc) => OpenAISocketHandler.handleAIGenerateSlideEvent(doc))
    Socket.getSocket().on('presentation: ai_slides_generator_error', () => OpenAISocketHandler.handleAIGenerateSlideError())
    Socket.getSocket().on('presentation: ai_template_description', (doc) => OpenAISocketHandler.handleAIGenerateTemplateDescription(doc))
    Socket.getSocket().on('slide: ai_word_cloud_smart_grouping', (doc) => OpenAISocketHandler.handleAIGenerateWordCloudSmartGrouping(doc))
    Socket.getSocket().on('slide: ai_open_ended_smart_grouping', (doc) => OpenAISocketHandler.handleAIGenerateOpenEndedSmartGrouping(doc))
    Socket.getSocket().on('slide: ai_open_ended_smart_grouping_submission_change', (doc) => OpenAISocketHandler.handleAIOpenEndedSmartGroupingSubmissionChange(doc))
    Socket.getSocket().on('presentation: doc_to_quiz_generated', (doc) => OpenAISocketHandler.handleDocToSlideGenerated(doc))

    Socket.getSocket().on('presentation: doc_to_quiz_import_convert_error', (doc) => OpenAISocketHandler.handleDocToSlideImportConvertError(doc))
    Socket.getSocket().on('presentation: update-slides', () => OpenAISocketHandler.handleUpdatePresentation())
    if (!store.getters['liveStatsV2/canUseReactionEmqx']) {
      Socket.on(CREATE_REACTION, (doc) => this._handleCreateReaction(editor, doc))
    }
    Socket.on('patch-presentation', (doc) => this._handlePatchPresentation(editor, doc))
    Socket.getSocket().on('annotationsUpdated', (doc) => handleAnnotationUpdated(doc))
  },

  trackingJoinPresentation: _.debounce((presenter, presentation, sockets) => {
    const arrKeySocketIds = Object.keys(sockets)
    if (arrKeySocketIds.length <= 0) {
      return
    }

    const userSockets = {}
    let countEditor = 0
    let countViewer = 0
    arrKeySocketIds.forEach((socketId) => {
      const userInfo = sockets[socketId]
      if (!userSockets[userInfo.id]) {
        userSockets[userInfo.id] = {
          ...userInfo,
          socketId,
        }
        if (userInfo.accountRole === workspace.VIEWER) {
          countViewer++
        } else {
          countEditor++
        }
      }
    })

    const keyStorage = `collab-user-count-${presentation.id}`
    const countUser = Object.keys(userSockets).length
    const oldCountUserCollab = storage.getValue(keyStorage, 0)
    if (oldCountUserCollab >= countUser || countUser <= 1) {
      return
    }
    storage.setValue(keyStorage, countUser)
    Utils.trackGA4AndMixpanel('Join co-editing session', {
      eventAction: 'Join co-editing session',
      presentationId: presentation.id,
      teamId: presentation.accountId,
      editorCount: countEditor,
      viewerCount: countViewer,
    })
  }, 3000),

  _handleSocketOnline(editor, doc) {
    this.trackingJoinPresentation(doc.presenter, editor.presentation, JSON.parse(JSON.stringify(editor.sockets)))
    if ((!editor.sockets[doc.socketId] || !editor.sockets[doc.socketId].id) && doc.presenter) {
      editor.sockets[doc.socketId] = doc.presenter
      editor.joinCollaborativeEditingRoom(editor.presentation)
    }
    this.refreshEditor(editor)
  },

  _handleSocketOffline(editor, doc) {
    // Skip on shared screen
    if (editor.$route.name === 'PresentationShare') return

    delete editor.sockets[doc.socketId]
    this.refreshEditor(editor)
  },

  refreshEditor(editor) {
    const presenters = []
    Object.keys(editor.sockets).forEach((k) => {
      const socket = editor.sockets[k]
      const idx = presenters.findIndex((p) => { return p.id === socket.id })
      if (idx === -1) {
        socket.isMyData = socket.id == editor.currentUser.id
        if (socket.id == editor.currentUser.id && editor.isGuestUser) {
          // Demo mode, show you as Guest
          socket.name = 'Guest'
        }
        presenters.push(socket)
      }
    })

    presenters.sort((a, b) => {
      if (editor.presentation.takenOverBy === a.id) return -1
      if (editor.presentation.takenOverBy === b.id) return 1
      if (a.id < b.id) return -1
      if (a.id > b.id) return 1
      return 0
    })

    editor.$store.state.presentationEditors = presenters
    if (editor.presentation.takenOverBy) {
      editor.$store.state.isDisableEditPresentation =
      editor.presentation.takenOverBy && editor.currentUser.id !== editor.presentation.takenOverBy

      // const checkOwner =
      //   // !editor.presentation.permissions && editor.presentation.ownerId === editor.$store.state.currentUser.id
      //   !editor.presentation.permissions && editor.presentation.userId === editor.$store.state.currentUser.id
      // const checkOwner =
      //   !editor.presentation.folderId || editor.presentation.folder.ownerId === editor.$store.state.currentUser.id
      const checkHasPermission =
        editor.presentation.permissions &&
        editor.presentation.permissions.includes(Constants.permissions.RENAME_PRESENTATION)

      editor.$store.state.canEditPresentation =
        // !editor.$store.state.isDisableEditPresentation && (checkOwner || checkHasPermission)
        !editor.$store.state.isDisableEditPresentation && checkHasPermission

      if (store.state.integrationPlatform === 'GoogleSlide') {
        store.state.isDisableEditPresentation = false
        store.state.canEditPresentation = true
      }
    }
  },

  _handleBackstageControl(editor, payload) {
    if (store.state.isEnableAddinPowerpoint) return
    const { socketId, type, data } = payload
    if (socketId === Socket.getSocketId()) return
    // Only update for other screens
    if (type === 'update-slide-active') {
      // ken defined change brainstorm tab delay
      // editor.updateSlideActiveAction(data)
      if (data.quizStatus === 5 || data.showCorrectOption) {
        editor._handleShowResultsOnQuizSlide()
      }
    } else if (type === 'start-quiz-on-other-screen') {
      EventBus.$emit('start-quiz-on-other-screen', data)
    } else if (type === 'backstage-control: hints') {
      if (data === 'show-hints') {
        EventBus.$emit('canvas-hints: show-hints-from-backstage')
      } else if (data === 'close-hints') {
        EventBus.$emit('canvas-hints: close-from-backstage')
      } else if (data.eventName === 'next-back-hints') {
        EventBus.$emit('canvas-hints: next-back-from-backstage', data.mode, data.swiperActiveIndex)
      }
    } else if (type === 'reload') {
      // Backstage: Whenever reload quiz on origin screen
      // -> set quiz scene to Lobby on other screens
      if (editor.slideActive.type === 'pickAnswer') {
        const updatedSlide = {
          quizStatus: Constants.QuizStatuses.LOBBY_SCREEN,
        }
        editor.updateSlideActiveAction(updatedSlide)
        updatedSlide.socketId = Socket.getSocketId()
        updatedSlide.accessCode = editor.$store.state.presentation.uniqueAccessCode
        updatedSlide.presentationId = editor.$store.state.presentation.id
        // if
        // updatedSlide.presenting = editor.$store.getters.isPresenting || editor.isBackstage
        SlideApi.updateSlideById(editor.$store.state.slideActive.id, updatedSlide)
          .then(() => {
            return true
          })
          .catch(() => {})
      }
    } else if (type === 'acceptTypeAnswer') {
      EventBus.$emit('accept-other-type-answers-from-backstage')
    }
  },

  async _handleTakeOverEvent(editor, doc) {
    if (editor.currentUser.id === editor.presentation.takenOverBy &&
      doc.takenOverBy !== editor.currentUser.id) {
      if (!doc.user) {
        Object.keys(editor.sockets).forEach((k) => {
          const socket = editor.sockets[k]

          if (doc.takenOverBy === socket.id) {
            editor.tookOverPresenter = socket
            editor.presentation.TakenOverByUser = socket
          }
        })
      } else {
        editor.tookOverPresenter = doc.user
        editor.presentation.TakenOverByUser = doc.user
      }

      if (!editor.isMSTeamsApp) {
        EventBus.$emit('clearOtherPopups')
        setTimeout(() => {
          editor.beTakenOverWhilepresenting = doc.presenting
          editor.isShowBeTakenOverModal = true
        }, 300)
      }
    }

    editor.presentation.takenOverBy = doc.takenOverBy

    this.refreshEditor(editor)

    // AHA-5372 Presenting > Khi đang presenting và bị take over, presenter đó chưa được chuyển về màn hình Editor
    if (doc.takenOverBy != editor.currentUser.id) {
      if (editor.isMSTeamsApp) {
        editor.isShowMSTeamsReloadModal = true
        return
      }
      editor.$store.dispatch(SET_CURRENT_SLIDE_MODE, EDIT_MODE)
      editor.$router.replace({
        name: 'PresentationDetail',
        params: {
          id: editor.$store.state.presentation.id,
          beTakenOver: true,
        },
      })
    } else {
      await editor.getDataPresentationDetail('_handleTakeOverEvent')

      // This is a hack. We should not doeditor.
      const currentSlideId = editor.$store.state.slideActive.id
      if (currentSlideId) {
        editor.$store.dispatch('updateSlideActive', {
          id: null,
        })
        setTimeout(() => {
          editor.$store.dispatch(SET_CURRENT_ACTIVE_SLIDE, currentSlideId)
          EventBus.$emit('eventHandleSetValueTransition')
        }, 0)
      }
    }
  },

  _handleAudienceCreateIdeaEvent(editor, doc) {
    // editor._checkAndUpdateHasResults()
    const matchSlide = editor.presentation.slides.find((slide) => slide.id === doc.slideId)

    if (!matchSlide
      || !matchSlide.SlideOptions
      || matchSlide.SlideOptions.length >= Constants.MAX_IDEAS_NUMBER) {
      return
    }

    let newSlideOptions = [...editor.slideActive.SlideOptions, doc]
    newSlideOptions = newSlideOptions
      .map((idea, index) => ({ ...idea, createdAtOrder: index + 1 }))

    editor.patchSlideByIdAction({
      id: doc.slideId,
      SlideOptions: newSlideOptions,
    })

    if (doc.slideId === editor.slideActive.id) {
      EventBus.$emit('handleSortIdeasByCreatedAt')
    }

    EventBus.$emit('handleScrollingAndShowScroll')
  },

  _handleAudienceVoteIdeaEvent: _.throttle((editor, doc) => {
    const index = editor.slideActive.SlideOptions.findIndex((option) => {
      return option.id === doc.slideOptionId
    })
    if (index === -1) return
    editor.slideActive.SlideOptions[index].votesCount = doc.numberOfVotes
    editor.updateSlideActiveAction(editor.slideActive)
  }, 300),

  _handleRemovePresenterEvent(editor, doc) {
    const { removedPresenters } = doc
    if (removedPresenters.indexOf(editor.currentUser.email) > -1) {
      Utils.showToastError(this.$i18nPresenter.t('You have been removed from this presentation'))
      window.setTimeout(() => {
        window.location.reload()
      }, 2000)
    }
  },

  _handleAddSlides(editor, payload) {
    const slideTypes = {}
    Constants.allTypeSections.forEach((item) => {
      item.slideTypes.forEach((typeObj) => {
        slideTypes[typeObj.type] = typeObj
      })
    })

    payload.slides.forEach((item) => {
      if (item.type) {
        item.typeObj = slideTypes[item.type]
      }
    })

    editor.$store.state.presentation.slides = editor.$store.state.presentation.slides.concat(
      payload.slides,
    )
  },

  async _handleOnReconnect(editor) {
    const maxCountReconnect = 50
    const resetCountReconnect = 600
    store.state.countReconnectSocket += 1
    if (store.state.countReconnectSocket > maxCountReconnect) {
      editor.joinCollaborativeEditingRoom(editor.presentation)
      if (store.state.countReconnectSocket > resetCountReconnect) {
        store.state.countReconnectSocket = 0
      }
      return
    }
    // khi bỏ check if 2 dòng code bên dưới sẽ xảy ra vấn đề khi
    // reconect trong khi đang chơi quiz sẽ bị lỗi, có thể còn nhiều lỗi khác
    if (!store.getters.isPresenting) {
      editor.sockets = {}
      await editor.getDataPresentationDetail('_handleOnReconnect')
    } else {
      handleSocketConnect()
    }
    editor.joinCollaborativeEditingRoom(editor.presentation)
  },

  _handleUpdateReport(editor, payload) {
    editor.patchPresentationAction({
      exportData: {
        ...editor.presentation.exportData,
        ...payload,
      },
    })
  },

  _handleCreateReaction: _.throttle((editor, message) => {
    const { audienceName, reactionType, ...partialPresentation } = message
    editor.patchPresentationAction(partialPresentation)
    // editor._checkAndUpdateHasResults()
    EventBus.$emit(CREATE_REACTION, { audienceName, reactionType })
  }, 200),

  _handlePatchPresentation(editor, payload) {
    // Reactions already set state, set again will results in flickering
    // https://ahaslides.atlassian.net/browse/AHA-7788
    if (payload.reactions) return
    editor.patchPresentationAction(payload)
  },

  async _handleCommentPresentation(editor, doc) {
    if (doc.type === 'new-message' && doc.data.socketId !== Socket.getSocketId()) {
      EventBus.$emit('presentation-new-comment', doc.data)
    }
  },

  _handlePresentingSlideUpdate(editor, doc) {
    editor.updateSlideActiveAction(doc.data)
  },
}
